/* eslint-disable react/prop-types */
import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { ZoneCreationProperties } from '../types/zoneMapType';
import { stringToColor } from './zoneMapColorHelper';

/**
 * Props for SelectionToolbar component
 */
export interface SelectionToolbarProps {
  /** Index of the currently active zone */
  activeZoneIdx: number;
  /** Map of all zones being created/edited */
  zones: Map<number, ZoneCreationProperties>;
  /** Callback when active zone changes */
  onActiveZoneChange: (zoneIdx: number) => void;
  /** Whether the toolbar controls should be disabled */
  disabled: boolean;
  /** Whether undo is available */
  canUndo?: boolean;
  /** Callback for undo operation */
  onUndo?: () => void;
  /** Number of fixtures in temp selection */
  tempSelectionCount?: number;
  /** Callback to commit temp selection */
  onCommitSelection?: () => void;
  /** Callback to clear temp selection */
  onClearSelection?: () => void;
}

/**
 * Get color for a zone based on its name
 * Uses the same color logic as the floorplan display
 */
const getZoneColor = (zoneName: string): string => {
  return stringToColor(zoneName);
};

/**
 * SelectionToolbar Component
 *
 * Provides controls for fixture selection operations including:
 * - Active zone indicator and switcher
 * - Undo functionality
 * - Temp selection commit/clear actions
 *
 * This toolbar appears above the floorplan viewer when in zone creation/copy mode.
 * The FloorplanViewer supports both click and box selection simultaneously.
 */
const SelectionToolbar: React.FC<SelectionToolbarProps> = React.memo(
  ({
    activeZoneIdx,
    zones,
    onActiveZoneChange,
    disabled,
    canUndo = false,
    onUndo,
    tempSelectionCount = 0,
    onCommitSelection,
    onClearSelection,
  }) => {
    const activeZone = zones.get(activeZoneIdx);
    const zoneName = activeZone?.zoneName || `Zone ${activeZoneIdx + 1}`;
    const zoneColor = getZoneColor(zoneName);
    const zoneArray = React.useMemo(() => Array.from(zones.entries()), [zones]);

    return (
      <Column backgroundColor="#f5f5f5" width="100%">
        <Row spacing="400" spacingInset="400" alignmentVertical="center" alignmentHorizontal="justify" width="100%">
          {/* Active Zone Indicator */}
          <Column spacing="200">
            <Text>Active Zone</Text>
            <Row spacing="200" alignmentVertical="center">
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: zoneColor,
                  borderRadius: '3px',
                  border: '1px solid #333',
                }}
                aria-hidden="true"
              />
              {zoneArray.length > 1 ? (
                <Select
                  value={activeZoneIdx.toString()}
                  onChange={(value: string) => onActiveZoneChange(parseInt(value, 10))}
                  disabled={disabled}
                  width={200}
                  aria-label="Select active zone"
                >
                  {zoneArray.map(([idx, zone]) => (
                    <SelectOption key={idx} value={idx.toString()} label={zone.zoneName || `Zone ${idx + 1}`} />
                  ))}
                </Select>
              ) : (
                <Text>{zoneName}</Text>
              )}
            </Row>
          </Column>

          {/* Temp Selection Actions */}
          {tempSelectionCount > 0 && (
            <Column spacing="200">
              <Text>Box Selection ({tempSelectionCount} fixtures)</Text>
              <Row spacing="200">
                <Button
                  type="primary"
                  onClick={onCommitSelection}
                  disabled={disabled}
                  size="small"
                  aria-label={`Commit selection of ${tempSelectionCount} fixtures`}
                >
                  Commit
                </Button>
                <Button
                  type="secondary"
                  onClick={onClearSelection}
                  disabled={disabled}
                  size="small"
                  aria-label="Clear temporary selection"
                >
                  Clear
                </Button>
              </Row>
            </Column>
          )}

          {/* Undo Button */}
          {canUndo && onUndo && (
            <Column>
              <Button
                type="secondary"
                onClick={onUndo}
                disabled={disabled || !canUndo}
                size="small"
                aria-label="Undo last selection"
              >
                Undo
              </Button>
            </Column>
          )}

          {/* Selection Instructions - Right aligned */}
          <Column spacing="200">
            <Text type="b100" color="secondary">
              Click fixtures to select • Hold Shift + Drag for box selection
            </Text>
          </Column>
        </Row>
      </Column>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';

export default SelectionToolbar;
